import { CopilotKit, useThreads } from '@copilotkit/react-core';
import { CopilotSidebar } from '@copilotkit/react-ui';
import '@copilotkit/react-ui/styles.css';
import { FocusTrapZone } from '@fluentui/react';
import { Send20Filled, Sparkle20Filled } from '@fluentui/react-icons';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { appSettings } from 'src/common';
import localeService from 'src/dataServices/LocaleService';
import { supportTelemetryService } from 'src/dataServices/SupportTelemetryService';
import { RootState } from 'src/state/reduxStore';
import { AssistantMessageWithCitations, citationTagRenderers } from './AssistantMessageWithCitations';
import { ChatErrorMessage } from './ChatErrorMessage';
import { ContactUsFeedback } from './ContactUsFeedback';
import { CopilotDisclaimer } from './CopilotDisclaimer';
import './copilotkit.scss';
import { CustomCopilotButton } from './CustomCopilotButton';
import { CustomCopilotHeader } from './CustomCopilotHeader';
import { CustomMessages } from './CustomMessages';
import { FeedbackContextProvider, useFeedbackContext } from './FeedbackContext';
import { SupportTicketAction } from './SupportTicketAction';
import { TicketFormContextProvider, useTicketFormContext } from './TicketFormActiveContext';
import { useCopilotHeroBarBridge } from './useCopilotHeroBarBridge';
import { useCopilotSidebarState } from './useCopilotSidebarState';
import { useCopilotSuggestions } from './useCopilotSuggestions';

const DisclaimerPortal: React.FC = () => {
  const [container, setContainer] = React.useState<Element | null>(null);

  React.useEffect(() => {
    setContainer(document.querySelector('.copilotKitInputContainer'));
  }, []);

  if (!container) return null;
  return createPortal(<CopilotDisclaimer />, container);
};

const ContactUsAgentContent: React.FC = () => {
  const { threadId } = useThreads();
  const trackedOpenedThreads = React.useRef(new Set<string>());

  const { sidebarOpen, setSidebarOpen } = useCopilotSidebarState();
  const { activeTicketFormClass, blockEnterToSend } = useTicketFormContext();
  const { thumbsUpIcon, thumbsDownIcon, onThumbsUp, onThumbsDown } = useFeedbackContext();

  const copilotConfig = useSelector((state: RootState) => state.agentVerification.copilotConfig);

  const suggestions = useCopilotSuggestions();

  // Append messages broadcast by the Contact Us hero bar (rendered outside this provider).
  useCopilotHeroBarBridge();

  return (
    <>
      <SupportTicketAction />
      <FocusTrapZone
        disabled={!sidebarOpen}
        isClickableOutsideFocusTrap
        role={sidebarOpen ? 'dialog' : undefined}
        aria-label={
          sidebarOpen
            ? localeService.getText('ContactUs', 'CustomCopilotHeaderTitle') ?? copilotConfig.sidebarTitle
            : undefined
        }
      >
        <div onKeyDownCapture={blockEnterToSend}>
          <CopilotSidebar
            className={activeTicketFormClass}
            icons={{
              sendIcon: <Send20Filled />,
              openIcon: <Sparkle20Filled />,
              activityIcon: null,
              thumbsUpIcon,
              thumbsDownIcon
            }}
            labels={{
              title: localeService.getText('ContactUs', 'CustomCopilotHeaderTitle') ?? copilotConfig.sidebarTitle,
              initial: localeService.getText('ContactUs', 'ChatInitialMessage') ?? copilotConfig.sidebarGreeting,
              placeholder:
                localeService.getText('ContactUs', 'ContactUsInitialMessage') ?? copilotConfig.sidebarPlaceholder
            }}
            suggestions={suggestions}
            Header={CustomCopilotHeader}
            Button={CustomCopilotButton}
            AssistantMessage={AssistantMessageWithCitations}
            Messages={CustomMessages}
            ErrorMessage={ChatErrorMessage}
            markdownTagRenderers={citationTagRenderers}
            hideStopButton
            onSetOpen={(isOpen) => {
              setSidebarOpen(isOpen);
              if (isOpen && threadId && !trackedOpenedThreads.current.has(threadId)) {
                trackedOpenedThreads.current.add(threadId);
                supportTelemetryService.trackAgentChatOpened(threadId);
              }
            }}
            onThumbsUp={onThumbsUp}
            onThumbsDown={onThumbsDown}
          />
        </div>
      </FocusTrapZone>
      <DisclaimerPortal />
      <ContactUsFeedback />
    </>
  );
};

export const ContactUsAgent: React.FC = () => (
  <CopilotKit
    enableInspector={false}
    showDevConsole={false}
    agent="support_agent"
    runtimeUrl={appSettings.copilotRuntimeUrl}
  >
    <TicketFormContextProvider>
      <FeedbackContextProvider>
        <ContactUsAgentContent />
      </FeedbackContextProvider>
    </TicketFormContextProvider>
  </CopilotKit>
);

